import type { Language } from "@pima/web-api/client";
import Cookies from "js-cookie";

/**
 * The UI's locale. It shares a value set with the database's content
 * `language` enum, and the app passes it as one when asking for translated
 * rows — so it is derived from the contract rather than restated. If the two
 * ever need to differ (a UI language with no content), that divergence gets
 * its own type instead of drifting silently here.
 */
export type SupportedLocale = Language;

/**
 * The UI's locale and a row's content language are different ideas that happen
 * to share a value set. Tying them means a change to the database's `language`
 * enum fails this file rather than silently letting the two drift — the point
 * being to force the conversation, not to claim they are the same thing.
 */
type SameSet<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
export type SupportedLocaleMatchesContentLanguage =
  SameSet<SupportedLocale, Language> extends true ? true : never;

const SUPPORTED_LOCALES: SupportedLocale[] = ["en-US", "de-DE"];
export const DEFAULT_LOCALE: SupportedLocale = "de-DE";
const COOKIE_KEY = "locale";

/**
 * Synchronously detect the user's locale.
 * Priority: cookie → browser language → default (de-DE).
 *
 * On the server (SSR) window is undefined, so this returns DEFAULT_LOCALE —
 * callers that need the real client locale must call it from a client-only
 * effect (see useLocaleSync), never at module init, so the client's first
 * render matches the server-rendered HTML.
 */
export function detectLocale(): SupportedLocale {
  if (typeof window === "undefined") {
    return DEFAULT_LOCALE;
  }

  const saved = Cookies.get(COOKIE_KEY);
  if (saved && SUPPORTED_LOCALES.includes(saved as SupportedLocale)) {
    return saved as SupportedLocale;
  }

  const browserLang = navigator?.language;
  const detected: SupportedLocale = browserLang?.startsWith("de")
    ? "de-DE"
    : "en-US";

  Cookies.set(COOKIE_KEY, detected);
  return detected;
}
